import axios from 'axios';

// API Configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL,
  TIMEOUT: 1000000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Clear invalid token
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      
      // Redirect to login
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Generic API service class
class ApiService {
  
  /**
   * Generic GET request
   */
  static async get(endpoint, params = {}, options = {}) {
    try {
      const response = await apiClient.get(endpoint, { params, ...options });
      return {
        success: true,
        data: response.data,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Generic POST request
   */
  static async post(endpoint, data = {}, options = {}) {
    try {
      const response = await apiClient.post(endpoint, data, options);
      return {
        success: true,
        data: response.data,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Generic PUT request
   */
  static async put(endpoint, data = {}, options = {}) {
    try {
      const response = await apiClient.put(endpoint, data, options);
      return {
        success: true,
        data: response.data,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Generic DELETE request
   */
  static async delete(endpoint, options = {}) {
    try {
      const response = await apiClient.delete(endpoint, options);
      return {
        success: true,
        data: response.data,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * File upload request
   */
  static async upload(endpoint, formData, onUploadProgress = null) {
    try {
      const response = await apiClient.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress
      });
      return {
        success: true,
        data: response.data,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Comprehensive error handler
   */
  static handleError(error) {
    // Network errors
    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      return {
        success: false,
        error: 'CONNECTION_ERROR',
        message: 'Unable to connect to server. Please check your connection.'
      };
    }

    // Timeout errors
    if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        error: 'TIMEOUT_ERROR',
        message: 'Request timeout. Please try again.'
      };
    }

    // Server responded with error
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.message || data?.error || data?.detail;

      const statusMessages = {
        400: 'Bad request. Please check your input.',
        401: 'Unauthorized. Please log in again.',
        403: 'Access forbidden.',
        404: 'Resource not found.',
        409: 'Conflict. Resource already exists.',
        422: 'Validation error.',
        429: 'Too many requests. Please try again later.',
        500: 'Internal server error.',
        502: 'Bad gateway.',
        503: 'Service unavailable.',
        504: 'Gateway timeout.'
      };

      return {
        success: false,
        error: `HTTP_${status}`,
        message: message || statusMessages[status] || `Request failed with status ${status}`,
        status: status,
        details: data
      };
    }

    // Unknown errors
    return {
      success: false,
      error: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred. Please try again.'
    };
  }

  /**
   * Authentication methods
   */
  static setAuthToken(token) {
    if (token && token !== 'undefined' && token.trim() !== '') {
      localStorage.setItem('authToken', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify it was stored
      // const storedToken = localStorage.getItem('authToken');
    } else {
      localStorage.removeItem('authToken');
      delete apiClient.defaults.headers.common['Authorization'];
    }
  }

  static getAuthToken() {
    return localStorage.getItem('authToken');
  }

  static clearAuth() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('userProfile');
    delete apiClient.defaults.headers.common['Authorization'];
  }

  static isAuthenticated() {
    return !!this.getAuthToken();
  }

  /**
   * User Profile methods
   */
  static async fetchUserProfile() {
    return this.get('/cms/v1/profile');
  }

  static getUserProfile() {
    const profile = localStorage.getItem('userProfile');
    return profile ? JSON.parse(profile) : null;
  }

  static setUserProfile(profileData) {
    localStorage.setItem('userProfile', JSON.stringify(profileData));
  }

  static clearUserProfile() {
    localStorage.removeItem('userProfile');
  }
}

// Specific service classes for different endpoints
export class ChargerService {
  static async getAllChargers(teamParams = {}) {
    const { teamId, page = 1, limit = 10 } = teamParams;
    return ApiService.get(`/cms/v1/team/${teamId}/chargers?page=${page - 1}&pageSize=${limit}`);
  }

  static async getChargerById(id) {
    return ApiService.get(`/chargers/${id}`);
  }

  static async startCharger(chargerId, connectorId = null) {
    const payload = {
      parameters: {},
      chargerId: chargerId,
      remoteOperationType: "RemoteStart",
      source: "FLEETOPS"
    };
    if (connectorId) {
      payload.parameters.connectorId = connectorId;
    }
    return ApiService.post('/cms/v1/remote/operation', payload);
  }

  static async stopCharger(chargerId, connectorId = null) {
    const payload = {
      parameters: {},
      chargerId: chargerId,
      remoteOperationType: "RemoteStop",
      source: "FLEETOPS"
    };
    if (connectorId) {
      payload.parameters.connectorId = connectorId;
    }
    return ApiService.post('/cms/v1/remote/operation', payload);
  }

  static async resetCharger(chargerId, connectorId = null) {
    const payload = {
      parameters: {
        resetType: "HARD"
      },
      chargerId: chargerId,
      remoteOperationType: "Reset",
      source: "FLEETOPS"
    };
    if (connectorId) {
      payload.parameters.connectorId = connectorId;
    }
    return ApiService.post('/cms/v1/remote/operation', payload);
  }

  static async getActiveTransactions(teamId, chargepointPk) {
    return ApiService.get(`/cms/v1/team/${teamId}/charger/${chargepointPk}/transactions/active`);
  }

  static async getOperationStatus(chargerId, taskId) {
    return ApiService.get(`/cms/v1/remote/operation/charger/${chargerId}/task/${taskId}`);
  }

  static async getChargerConnectors(teamId, chargerId) {
    return ApiService.get(`/cms/v1/team/${teamId}/charger/${chargerId}/connectors`);
  }

  static async getRfidTags(teamId, chargerId, payload) {
    return ApiService.post(`/cms/v1/team/${teamId}/tags/remoteop/${chargerId}`, payload);
  }
}

export class SessionService {
  static async getActiveSessions(teamParams = {}) {
    return ApiService.get(`/cms/v1/team/${teamParams.teamId}/dashboard_stats/active_sessions/details?page=1&limit=10`);
  }

  static async getSessionById(id) {
    return ApiService.get(`/sessions/${id}`);
  }

  static async getSessionHistory() {
    return ApiService.get('/sessions/history');
  }
}

export class TransactionService {
  static async getAllTransactions(teamParams = {}) {
    const { teamId, page = 1, limit = 10, filterRfid = true } = teamParams;
    return ApiService.get(`/cms/v1/team/${teamId}/transactions?page=${page - 1}&pageSize=${limit}&filterRfid=${filterRfid}`);
  }

  static async downloadTransactionsExcel(teamParams = {}) {
    const { teamId, filterRfid = true } = teamParams;
    return ApiService.get(
      `/cms/v1/team/${teamId}/transactions/excel_download`,
      { filterRfid },
      {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream'
        }
      }
    );
  }

  static async getTransactionById(id) {
    return ApiService.get(`/transactions/${id}`);
  }

  static async getTodayTransactions() {
    return ApiService.get('/transactions/today');
  }
}

export class AlertService {
  static async getAlerts() {
    return ApiService.get('/alerts');
  }

  static async getRecentAlerts() {
    return ApiService.get('/alerts/recent');
  }

  static async acknowledgeAlert(id) {
    return ApiService.post(`/alerts/${id}/acknowledge`);
  }
}

export class DashboardService {
  static async getStats(teamParams = {}) {
    return ApiService.get(`cms/v1/team/${teamParams.teamId}/dashboard_stats/analytics`);
  }

  static async getOverview() {
    return ApiService.get('/dashboard/overview');
  }
}

export default ApiService;
export { apiClient, API_CONFIG };